import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MessageCircle } from "lucide-react";

const PRESET = {
  HEALTHY: { txt: "Operacional", dot: "bg-emerald-500", ring: "border-emerald-200" },
  DEGRADED: { txt: "Canal degradado", dot: "bg-amber-500", ring: "border-amber-200" },
  PAUSED: { txt: "Envios pausados", dot: "bg-zinc-400", ring: "border-zinc-200" },
  BLOCKED: { txt: "Envios pausados", dot: "bg-zinc-400", ring: "border-zinc-200" },
  DISCONNECTED: { txt: "Desligado", dot: "bg-red-500", ring: "border-red-200" },
};

// Discreet global WhatsApp channel indicator. Shows only when there is activity
// (queue) or the channel is not healthy. Polls every 20s. Admin/gestor only.
export default function OutboxStatusWidget() {
  const [m, setM] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/organizations/me/whatsapp/outbox/metrics")
      .then(({ data }) => { if (alive) setM(data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!m) return null;
  const status = m.provider_status || "HEALTHY";
  const active = m.queued > 0 || m.sending > 0 || m.rate_limited > 0;
  // Keep the UI calm: hide entirely when healthy and idle.
  if (status === "HEALTHY" && !active) return null;
  const p = PRESET[status] || PRESET.HEALTHY;

  return (
    <div className="relative" ref={ref} data-testid="outbox-widget">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="outbox-widget-btn"
        className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border ${p.ring} bg-white hover:bg-zinc-50 transition-colors`}
      >
        <span className={`w-2 h-2 rounded-full ${p.dot} ${status !== "HEALTHY" ? "animate-pulse" : ""}`} />
        <MessageCircle className="w-3.5 h-3.5 text-zinc-500" />
        {m.queued > 0 && <span className="text-xs font-medium text-zinc-700" data-testid="outbox-widget-queued">{m.queued}</span>}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-xl border border-zinc-200 bg-white shadow-lg p-4 z-50" data-testid="outbox-widget-popover">
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-2 h-2 rounded-full ${p.dot}`} />
            <span className="text-sm font-semibold text-zinc-900">WhatsApp — {p.txt}</span>
          </div>
          {status === "HEALTHY" ? (
            <p className="text-xs text-zinc-500 mb-3">Os envios estão a decorrer normalmente.</p>
          ) : status === "DEGRADED" ? (
            <p className="text-xs text-zinc-500 mb-3">Os envios estão a ser processados de forma limitada. Nenhuma mensagem será perdida.</p>
          ) : (
            <p className="text-xs text-zinc-500 mb-3">Os envios estão temporariamente pausados. As mensagens permanecem em fila e serão enviadas assim que possível.</p>
          )}
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="rounded-lg bg-zinc-50 py-2">
              <div className="text-base font-semibold text-zinc-900" data-testid="outbox-widget-inqueue">{m.queued}</div>
              <div className="text-[11px] text-zinc-500">em fila</div>
            </div>
            <div className="rounded-lg bg-zinc-50 py-2">
              <div className="text-base font-semibold text-zinc-900">{m.sent_last_1h}</div>
              <div className="text-[11px] text-zinc-500">enviadas 1h</div>
            </div>
            {m.rate_limited > 0 && (
              <div className="rounded-lg bg-amber-50 py-2 col-span-2">
                <div className="text-base font-semibold text-amber-700">{m.rate_limited}</div>
                <div className="text-[11px] text-amber-600">a aguardar janela de envio</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
